// Functions to get all the queries we need to satisfy the api
// api.ts
import axios from "axios";
import { tokenStorage } from "./tokenStorage.tsx";
import {
  PostListenTimeRequest,
  GenericMessageResponse,
  GetLessonDataRequest,
  GetLessonDataResponse,
  PostActivityCompleteRequest,
  PostActivityCompleteResponse,
  PostLessonCompletedRequest,
  PostChapterCompletedResponse,
  PostChapterCompletedRequest,
  PutWordStatusRequest,
  PutWordStatusResponse,
  GetCourseInfoResponse,
  PostLessonCompletedResponse,
  GetCardsResponse,
  DeleteCardRequest,
  GetUserInfoResponse,
  GetUserStatsResponse,
  GetStatsTableRequest,
  GetStatsTableResponse,
  GetStreakDaysRequest,
  GetStreakDaysResponse,
  WebSignupV2Request,
  WebSignupV2Response,
  GetLearningWordsDictionaryResponse,
  GetKnownWordsDictionaryResponse,
  GetCardCountResponse,
  GetDictionaryStatsResponse,
  GetAllLessonRequest,
  GetAllLessonsResponse,
  GetAllChaptersRequest,
  PutCardRequest,
  PutCardRequestResponse,
  ClientUpdateHeaderKey,
  DevicePlatformHeader,
  GetAllCoursesResponse,
  PostCourseCompletedResponse,
  PostCourseCompletedRequest,
  PutNameRequest,
  StripeCheckoutResponse,
  GetAllCourseTreesResponse,
  PostWordErrorRequest,
  NPSCompletedResponse,
  GetPhraseRequest,
  GetPhraseResponse,
  PutPhraseSavedRequest,
  GetAllPhrasesResponse,
  PutUserPhraseStatusRequest,
  GetSearchWordsResponse,
  GetSearchPhrasesResponse,
  PostOnboardingRequest,
  Onboarding,
  GetUserSettingsResponse,
  PutUserSettingsRequest,
  GetAllCoursesProgressResponse,
  GetAllCourseTopicsResponse,
  GetAllCourseLevelsResponse,
  GetCompletedLessonsResponse,
  PostPlacementResultRequest,
  TutorialItemID,
  PostCompleteTutorialItemRequest,
  PutWordStatusBulkRequest,
  WaterDropletResponse,
  AppRatingEligibilityResponse,
  TranslateRequest,
  TranslateResponse,
  BulkAddRequest,
  BulkAddResponse,
  ExplainRequest,
  ExplainStreamEvent,
} from "@shared_types";
import { API_BASE_URL, apiUrl, BAD_AUTH_REDIRECT_URL } from "./base";
import { signOutUser } from "./login.tsx";
import VERSION from "@/version";

export const axiosInstance = axios.create({
  baseURL: API_BASE_URL,
});

axiosInstance.interceptors.request.use((config) => {
  config.headers[ClientUpdateHeaderKey] = VERSION;
  config.headers[DevicePlatformHeader] = "web";
  // Parity with iOS (client-native/src/api/Api.ts sets X-Timezone from
  // expo-localization): the backend (usage_tracking_middleware) only updates the
  // user's stored timezone when this header is present, and uses it for
  // notification timing + day-boundary/streak math. Without it a web-primary
  // user's tz stays at the DB default (UTC), skewing streaks/notifications.
  try {
    config.headers["X-Timezone"] =
      Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    config.headers["X-Timezone"] = "UTC";
  }
  return config;
});

export class AuthError extends Error {
  constructor(message: string) {
    super(message);
  }
}

// Error handling
function handleAxiosError(error: unknown): never {
  const axiosError = axios.isAxiosError(error) ? error : undefined;
  const status = axiosError?.response?.status;
  const unAuthorizedStatus = 401;

  // anytime an unathorized request goes through in our app,
  // this will redirect the user to where we want em
  if (status == unAuthorizedStatus) {
    // redirect user to onBoarding
    window.location.assign(BAD_AUTH_REDIRECT_URL);

    // clean the token from storage since its bad and wont be useful anymore
    tokenStorage.clearToken();
    throw new AuthError("unauthorized");
  }

  let message = error instanceof Error ? error.message : "Request failed";

  if (
    axiosError?.response?.data &&
    typeof axiosError.response.data === "object" &&
    "message" in axiosError.response.data
  ) {
    const { message: responseMessage } = axiosError.response.data;
    if (typeof responseMessage === "string") {
      message = responseMessage;
    }
  }

  console.error(`Error: ${message} (Status Code: ${status})`);
  throw new Error(message);
}

// this function checks to see if we are logged in.
// this means the token in local storage is valid
// we want to minimize reliance on pocketbase which has a
// changing api
export async function isLoggedIn(): Promise<boolean> {
  if (!tokenStorage.isTokenSet()) {
    return false;
  }
  const token = tokenStorage.getToken();
  const valid = await isTokenValid(token);
  return valid;
}

async function isTokenValid(token: string): Promise<boolean> {
  // try a small endpoint with auth
  // if we get a 401 return false
  // improvement could be to make an enpoint just for this,
  // incase this one changes
  try {
    await axiosInstance.get("/auth/userinfo", {
      headers: { Authorization: `Bearer ${token}` },
    });
    return true;
  } catch (error) {
    const badAuth = axios.isAxiosError(error) && error.response?.status == 401;
    if (badAuth) {
      return false;
    }
    return true;
  }
}

/**
 * Silently refresh the auth token to get a new long-lived token from the server.
 * Called once on app startup after confirming the user is logged in.
 * This ensures existing users with short-lived tokens get upgraded to
 * never-expiring tokens without being logged out.
 */
export async function refreshAuthToken(): Promise<void> {
  try {
    const token = tokenStorage.getToken();
    if (!token) return;

    const response = await axiosInstance.post(
      "/api/collections/users/auth-refresh",
      {},
      { headers: { Authorization: `Bearer ${token}` } }
    );

    const newToken = response.data?.token;
    if (newToken) {
      tokenStorage.setToken(newToken);
      console.log("[auth] Token refreshed successfully");
    }
  } catch (error) {
    // Don't log out the user if refresh fails — their current token still works
    console.warn("[auth] Token refresh failed (non-fatal):", error);
  }
}

// Define a generic type for the functions that will be passed into callWithAuth
type AuthFunction<T, R> = (args: { authToken: string; data?: T }) => Promise<R>;

// callWithAuth function definition
// It tries to read the token from local storage
// if its missing we will log an error and redirect.
// The request will be a type defined in this file for the request
// the browser to login page
// GetRequest need to use params
export function callWithAuth<T, R>(
  f: AuthFunction<T, R>,
  data?: T
): Promise<R> {
  if (!tokenStorage.isTokenSet()) {
    window.location.assign(BAD_AUTH_REDIRECT_URL);
  }
  const authToken = tokenStorage.getToken();
  const params: AuthFuncParams<T> = {
    authToken,
    data,
  };
  return f(params);
}

// All get has params and not request
export interface AuthFuncParams<T> {
  authToken: string;
  data?: T;
}

// API Request Functions
export async function postListenTime({
  data,
  authToken,
}: AuthFuncParams<PostListenTimeRequest>): Promise<GenericMessageResponse> {
  const request = data;
  try {
    const response = await axiosInstance.post<GenericMessageResponse>(
      "/auth/listentime",
      request,
      {
        headers: { Authorization: `Bearer ${authToken}` },
      }
    );
    return response.data;
  } catch (error) {
    handleAxiosError(error);
  }
}

export async function postNewUser({
  authToken,
}: AuthFuncParams<void>): Promise<GenericMessageResponse> {
  try {
    const response = await axiosInstance.post<GenericMessageResponse>(
      "/auth/newuser",
      undefined,
      {
        headers: { Authorization: `Bearer ${authToken}` },
      }
    );
    return response.data;
  } catch (error) {
    handleAxiosError(error);
  }
}

export async function deleteUser(): Promise<void> {
  try {
    const authToken = tokenStorage.getToken();
    await axiosInstance.delete<void>("/auth/deleteuser", {
      headers: { Authorization: `Bearer ${authToken}` },
    });
    signOutUser();
  } catch (error) {
    handleAxiosError(error);
  }
}

export async function getLessonData({
  data,
  authToken,
}: AuthFuncParams<GetLessonDataRequest>): Promise<GetLessonDataResponse> {
  const params = data;
  try {
    const response = await axiosInstance.get<GetLessonDataResponse>(
      "/auth/lessondata",
      {
        params,
        headers: { Authorization: `Bearer ${authToken}` },
      }
    );
    return response.data;
  } catch (error) {
    handleAxiosError(error);
  }
}

export async function postChapterCompleted({
  data,
  authToken,
}: AuthFuncParams<PostChapterCompletedRequest>): Promise<PostChapterCompletedResponse> {
  const request = data;
  try {
    const response = await axiosInstance.post<PostChapterCompletedResponse>(
      "/auth/chaptercompleted",
      request,
      {
        headers: { Authorization: `Bearer ${authToken}` },
      }
    );
    return response.data;
  } catch (error) {
    handleAxiosError(error);
  }
}
export async function postCourseCompleted({
  data,
  authToken,
}: AuthFuncParams<PostCourseCompletedRequest>): Promise<PostCourseCompletedResponse> {
  const request = data;
  try {
    const response = await axiosInstance.post<PostCourseCompletedResponse>(
      "/auth/coursecompleted",
      request,
      {
        headers: { Authorization: `Bearer ${authToken}` },
      }
    );
    return response.data;
  } catch (error) {
    handleAxiosError(error);
  }
}

export async function postLessonCompleted({
  data,
  authToken,
}: AuthFuncParams<PostLessonCompletedRequest>): Promise<PostLessonCompletedResponse> {
  const request = data;
  try {
    const response = await axiosInstance.post<PostLessonCompletedResponse>(
      "/auth/lessoncompleted",
      request,
      {
        headers: { Authorization: `Bearer ${authToken}` },
      }
    );
    return response.data;
  } catch (error) {
    handleAxiosError(error);
  }
}

export async function putWordStatusBulk({
  data,
  authToken,
}: AuthFuncParams<PutWordStatusBulkRequest>): Promise<WaterDropletResponse> {
  const request = data;
  try {
    const response = await axiosInstance.put<WaterDropletResponse>(
      "/auth/wordstatus/bulk",
      request,
      {
        headers: { Authorization: `Bearer ${authToken}` },
      }
    );
    return response.data;
  } catch (error) {
    handleAxiosError(error);
  }
}

export async function putWordStatus({
  data,
  authToken,
}: AuthFuncParams<PutWordStatusRequest>): Promise<PutWordStatusResponse> {
  const request = data;
  try {
    const response = await axiosInstance.put<PutWordStatusResponse>(
      "/auth/wordstatus",
      request,
      {
        headers: { Authorization: `Bearer ${authToken}` },
      }
    );
    return response.data;
  } catch (error) {
    handleAxiosError(error);
  }
}

export async function postActivityComplete({
  data,
  authToken,
}: AuthFuncParams<PostActivityCompleteRequest>): Promise<PostActivityCompleteResponse> {
  const request = data;
  try {
    const response = await axiosInstance.post<PostActivityCompleteResponse>(
      "/auth/activitycomplete",
      request,
      {
        headers: { Authorization: `Bearer ${authToken}` },
      }
    );
    return response.data;
  } catch (error) {
    handleAxiosError(error);
  }
}

export async function getAllCourses({
  authToken,
}: AuthFuncParams<void>): Promise<GetAllCoursesResponse> {
  try {
    const response = await axiosInstance.get<GetAllCoursesResponse>(
      "/auth/courses",
      {
        headers: { Authorization: `Bearer ${authToken}` },
      }
    );
    return response.data;
  } catch (error) {
    handleAxiosError(error);
  }
}

export async function getAllCourseTrees({
  authToken,
}: AuthFuncParams<void>): Promise<GetAllCourseTreesResponse> {
  try {
    const response = await axiosInstance.get<GetAllCourseTreesResponse>(
      "/auth/coursetrees",
      {
        headers: { Authorization: `Bearer ${authToken}` },
      }
    );
    return response.data;
  } catch (error) {
    handleAxiosError(error);
  }
}

export async function getAllLessons({
  data,
  authToken,
}: AuthFuncParams<GetAllLessonRequest>): Promise<GetAllLessonsResponse> {
  const params = data;
  try {
    const response = await axiosInstance.get<GetAllLessonsResponse>(
      "/auth/chapterinfo",
      {
        params,
        headers: { Authorization: `Bearer ${authToken}` },
      }
    );
    return response.data;
  } catch (error) {
    handleAxiosError(error);
  }
}

export async function getAllChapters({
  data,
  authToken,
}: AuthFuncParams<GetAllChaptersRequest>): Promise<GetCourseInfoResponse> {
  const params = data;
  try {
    // Use the V2 course-info endpoint (iOS parity — client-native/src/api/Api.ts
    // calls /auth/v2/courseinfo). V1 (/auth/courseinfo) filters chapters by
    // has_activities, so courses whose chapters have no activities (the "Shorts"
    // courses — e.g. Basic Words Shorts, Grammar A0 Shorts) come back with zero
    // chapters and the Learn tab falls into its "No course selected" empty state
    // even though the course is selected. V2 returns ALL published chapters.
    const response = await axiosInstance.get<GetCourseInfoResponse>(
      "/auth/v2/courseinfo",
      {
        params,
        headers: { Authorization: `Bearer ${authToken}` },
      }
    );
    return response.data;
  } catch (error) {
    handleAxiosError(error);
  }
}

export async function getDictionaryStats({
  authToken,
}: AuthFuncParams<void>): Promise<GetDictionaryStatsResponse> {
  try {
    const response = await axiosInstance.get<GetDictionaryStatsResponse>(
      "/auth/dictionarystats",
      {
        headers: { Authorization: `Bearer ${authToken}` },
      }
    );
    return response.data;
  } catch (error) {
    handleAxiosError(error);
  }
}

export async function getKnownWordsDictionary({
  authToken,
  data,
}: AuthFuncParams<{
  page?: number;
  limit?: number;
}>): Promise<GetKnownWordsDictionaryResponse> {
  try {
    const response = await axiosInstance.get<GetKnownWordsDictionaryResponse>(
      "/auth/knownwords",
      {
        params: data,
        headers: { Authorization: `Bearer ${authToken}` },
      }
    );
    return response.data;
  } catch (error) {
    handleAxiosError(error);
  }
}

export async function getLearningWordsDictionary({
  authToken,
  data,
}: AuthFuncParams<{
  page?: number;
  limit?: number;
}>): Promise<GetLearningWordsDictionaryResponse> {
  try {
    const response =
      await axiosInstance.get<GetLearningWordsDictionaryResponse>(
        "/auth/learningwords",
        {
          params: data,
          headers: { Authorization: `Bearer ${authToken}` },
        }
      );
    return response.data;
  } catch (error) {
    handleAxiosError(error);
  }
}

export async function getReviewCards({
  authToken,
}: AuthFuncParams<void>): Promise<GetCardsResponse> {
  try {
    const response = await axiosInstance.get<GetCardsResponse>("/auth/cards", {
      headers: { Authorization: `Bearer ${authToken}` },
    });
    return response.data;
  } catch (error) {
    handleAxiosError(error);
  }
}

export async function getCardCount({
  authToken,
}: AuthFuncParams<void>): Promise<GetCardCountResponse> {
  try {
    const response = await axiosInstance.get<GetCardCountResponse>(
      "/auth/card/count",
      {
        headers: { Authorization: `Bearer ${authToken}` },
      }
    );
    return response.data;
  } catch (error) {
    handleAxiosError(error);
  }
}

export async function putReviewCard({
  authToken,
  data,
}: AuthFuncParams<PutCardRequest>): Promise<PutCardRequestResponse> {
  try {
    const response = await axiosInstance.put<PutCardRequestResponse>(
      "/auth/card",
      data,
      {
        headers: { Authorization: `Bearer ${authToken}` },
      }
    );
    return response.data;
  } catch (error) {
    handleAxiosError(error);
  }
}

export async function deleteCard({
  authToken,
  data,
}: AuthFuncParams<DeleteCardRequest>): Promise<GenericMessageResponse> {
  try {
    const response = await axiosInstance.delete<GenericMessageResponse>(
      "/auth/deletecard/" + data?.cardId,
      {
        headers: { Authorization: `Bearer ${authToken}` },
        data: { card_id: data?.cardId },
      }
    );
    return response.data;
  } catch (error) {
    handleAxiosError(error);
  }
}

export async function getUserInfoNoRedirect({
  authToken,
}: AuthFuncParams<void>): Promise<GetUserInfoResponse | undefined> {
  try {
    const response = await axiosInstance.get<GetUserInfoResponse>(
      "/auth/userinfo",
      {
        headers: { Authorization: `Bearer ${authToken}` },
      }
    );
    return response.data;
  } catch (error) {
    return undefined;
  }
}

export async function getUserInfo({
  authToken,
}: AuthFuncParams<void>): Promise<GetUserInfoResponse> {
  try {
    const response = await axiosInstance.get<GetUserInfoResponse>(
      "/auth/userinfo",
      {
        headers: { Authorization: `Bearer ${authToken}` },
      }
    );
    return response.data;
  } catch (error) {
    handleAxiosError(error);
  }
}

export async function getUserStats({
  authToken,
}: AuthFuncParams<void>): Promise<GetUserStatsResponse> {
  try {
    const response = await axiosInstance.get<GetUserStatsResponse>(
      "/auth/userstats",
      {
        headers: { Authorization: `Bearer ${authToken}` },
      }
    );
    return response.data;
  } catch (error) {
    handleAxiosError(error);
  }
}

export async function getStatsTable({
  data,
  authToken,
}: AuthFuncParams<GetStatsTableRequest>): Promise<GetStatsTableResponse> {
  const params = data;
  try {
    const response = await axiosInstance.get<GetStatsTableResponse>(
      "/auth/statstable",
      {
        params,
        headers: { Authorization: `Bearer ${authToken}` },
      }
    );
    return response.data;
  } catch (error) {
    handleAxiosError(error);
  }
}

/**
 * Days the user logged listening activity within [startDate, endDate] (iOS
 * `getStreakDays`, `/auth/streakdays`). Each StatsData entry is a YYYY-MM-DD
 * date with a value; the home week strip uses these so days with activity
 * outside the consecutive-streak window still show completed.
 */
export async function getStreakDays({
  data,
  authToken,
}: AuthFuncParams<GetStreakDaysRequest>): Promise<GetStreakDaysResponse> {
  try {
    const response = await axiosInstance.get<GetStreakDaysResponse>(
      "/auth/streakdays",
      {
        params: { startDate: data!.startDate, endDate: data!.endDate },
        headers: { Authorization: `Bearer ${authToken}` },
      }
    );
    return response.data;
  } catch (error) {
    handleAxiosError(error);
  }
}

export async function postWebSignupV2(
  data: WebSignupV2Request
): Promise<WebSignupV2Response> {
  try {
    const authToken = tokenStorage.getToken();
    const response = await axiosInstance.post<WebSignupV2Response>(
      "/auth/v2/web-signup",
      data,
      {
        headers: authToken ? { Authorization: `Bearer ${authToken}` } : {},
      }
    );
    return response.data;
  } catch (error) {
    handleAxiosError(error);
  }
}

export async function postGuestWebSignupV2(
  data: WebSignupV2Request
): Promise<WebSignupV2Response> {
  try {
    const response = await axiosInstance.post<WebSignupV2Response>(
      "/guest/web-signup",
      data
    );
    return response.data;
  } catch (error) {
    handleAxiosError(error);
  }
}

export interface PostFirebasePushTokenRequest {
  token: string;
}

export async function postFirebasePushToken({
  data,
  authToken,
}: AuthFuncParams<PostFirebasePushTokenRequest>): Promise<GenericMessageResponse> {
  const request = data;
  try {
    const response = await axiosInstance.post<GenericMessageResponse>(
      "/auth/firebase",
      request,
      {
        headers: { Authorization: `Bearer ${authToken}` },
      }
    );
    return response.data;
  } catch (error) {
    handleAxiosError(error);
  }
}

export interface PostAppleVerifyTransactionRequest {
  transactionId: string;
}

export async function postAppleVerifyTransaction({
  data,
  authToken,
}: AuthFuncParams<PostAppleVerifyTransactionRequest>): Promise<GenericMessageResponse> {
  const request = data;
  try {
    const response = await axiosInstance.post<GenericMessageResponse>(
      "/auth/apple/iap/verify",
      request,
      {
        headers: { Authorization: `Bearer ${authToken}` },
      }
    );
    return response.data;
  } catch (error) {
    handleAxiosError(error);
  }
}

type CheckoutSessionRequest = {
  sku: string;
};

export async function checkoutSession({
  data,
  authToken,
}: AuthFuncParams<CheckoutSessionRequest>): Promise<StripeCheckoutResponse> {
  if (!data) {
    throw new Error("SKU is required");
  }
  try {
    const response = await axiosInstance.get<StripeCheckoutResponse>(
      "/auth/stripe/checkout/" + data.sku,
      {
        headers: { Authorization: `Bearer ${authToken}` },
      }
    );
    return response.data;
  } catch (error) {
    handleAxiosError(error);
  }
}

export async function webSignupCheckoutSession(
  data: CheckoutSessionRequest
): Promise<StripeCheckoutResponse> {
  if (!data) {
    throw new Error("SKU is required");
  }
  try {
    const response = await axiosInstance.get<StripeCheckoutResponse>(
      "/guest/stripe/checkout/" + data.sku
    );
    return response.data;
  } catch (error) {
    handleAxiosError(error);
  }
}

export interface SessionStatusRequest {
  sessionId: string;
  sku: string;
}

export interface SessionStatusResponse {
  sessionStatus: string;
  paymentStatus: string;
}

export async function sessionStatus({
  data,
  authToken,
}: AuthFuncParams<SessionStatusRequest>): Promise<SessionStatusResponse> {
  const request = data;
  if (!request) {
    throw new Error("Session ID and SKU are required");
  }
  try {
    const response = await axiosInstance.get<SessionStatusResponse>(
      "/auth/stripe/sessionstatus/" + request.sessionId,
      {
        params: {
          sku: request.sku,
        },
        headers: { Authorization: `Bearer ${authToken}` },
      }
    );
    return response.data;
  } catch (error) {
    handleAxiosError(error);
  }
}

export async function webSignupSessionStatus(
  request: SessionStatusRequest
): Promise<SessionStatusResponse> {
  if (!request) {
    throw new Error("Session ID and SKU are required");
  }
  try {
    const response = await axiosInstance.get<SessionStatusResponse>(
      "/guest/stripe/sessionstatus/" + request.sessionId,
      {
        params: {
          sku: request.sku,
        },
      }
    );
    return response.data;
  } catch (error) {
    handleAxiosError(error);
  }
}

export interface PostCreateCustomerPortalSessionResponse {
  url: string;
}

export async function postCreateCustomerPortalSession({
  authToken,
}: AuthFuncParams<void>): Promise<PostCreateCustomerPortalSessionResponse> {
  try {
    const response =
      await axiosInstance.post<PostCreateCustomerPortalSessionResponse>(
        "/auth/stripe/create-customer-portal-session",
        undefined,
        {
          headers: { Authorization: `Bearer ${authToken}` },
        }
      );
    return response.data;
  } catch (error) {
    handleAxiosError(error);
  }
}

export enum CardAction {
  Good = "good",
  Again = "again",
}

export interface GetCurrentCourseResponse {
  /** Undefined when the user hasn't selected a course yet (no spinner / "Select a course"). */
  courseId?: number;
}

const CURRENT_COURSE_KEY = "currentCourse";

/**
 * Get the current course the user is working on. Returns no courseId when none
 * has been selected — callers gate their queries on this (see LearnPage /
 * CoursesPage / CourseView) and render the "Select a course" empty state rather
 * than fabricating course 1 (which forced a slow fetch + endless home spinner).
 */
export async function getCurrentCourse(): Promise<GetCurrentCourseResponse> {
  const currentCourse = localStorage.getItem(CURRENT_COURSE_KEY);
  if (!currentCourse) return {};
  try {
    return JSON.parse(currentCourse);
  } catch {
    localStorage.removeItem(CURRENT_COURSE_KEY);
    return {};
  }
}

export interface PutCurrentCourseRequest extends GetCurrentCourseResponse {}

/**
 * Set the current course the user is working on.
 */
export async function putCurrentCourse(
  data: PutCurrentCourseRequest
): Promise<void> {
  localStorage.setItem(CURRENT_COURSE_KEY, JSON.stringify(data));
}

const ACTIVE_COURSE_FILTERS_KEY = "activeCourseFilters";

/**
 * Get the active course filters.
 */
export async function getActiveCourseFilters(): Promise<Filters> {
  const filters = localStorage.getItem(ACTIVE_COURSE_FILTERS_KEY);
  if (!filters) return { selectedLevel: 0, selectedTopics: [] };
  try {
    return JSON.parse(filters);
  } catch {
    localStorage.removeItem(ACTIVE_COURSE_FILTERS_KEY);
    return { selectedLevel: 0, selectedTopics: [] };
  }
}

export type Filters = {
  selectedLevel: number | null;
  selectedTopics: number[];
};
/**
 * Set the active course filters.
 */
export async function putActiveCourseFilters(data: Filters): Promise<void> {
  localStorage.setItem(ACTIVE_COURSE_FILTERS_KEY, JSON.stringify(data));
}

export async function putName({
  data,
  authToken,
}: AuthFuncParams<PutNameRequest>): Promise<GenericMessageResponse> {
  const request = data;
  try {
    const response = await axiosInstance.put<GenericMessageResponse>(
      "/auth/name",
      request,
      {
        headers: { Authorization: `Bearer ${authToken}` },
      }
    );
    return response.data;
  } catch (error) {
    handleAxiosError(error);
  }
}

export interface PostNPSRequest {
  rating: number;
}

export async function postNPS({
  data,
  authToken,
}: AuthFuncParams<PostNPSRequest>): Promise<GenericMessageResponse> {
  const request = data;
  try {
    const response = await axiosInstance.post<GenericMessageResponse>(
      "/auth/nps",
      request,
      {
        headers: { Authorization: `Bearer ${authToken}` },
      }
    );
    return response.data;
  } catch (error) {
    handleAxiosError(error);
  }
}

export interface PostNPSFeedbackRequest {
  feedback: string;
}

export async function postNPSFeedback({
  data,
  authToken,
}: AuthFuncParams<PostNPSFeedbackRequest>): Promise<GenericMessageResponse> {
  const request = data;
  try {
    const response = await axiosInstance.post<GenericMessageResponse>(
      "/auth/nps/feedback",
      request,
      {
        headers: { Authorization: `Bearer ${authToken}` },
      }
    );
    return response.data;
  } catch (error) {
    handleAxiosError(error);
  }
}

export async function getShouldSeeNPS({
  authToken,
}: AuthFuncParams<void>): Promise<NPSCompletedResponse> {
  try {
    const response = await axiosInstance.get<NPSCompletedResponse>(
      "/auth/nps/should-see",
      {
        headers: { Authorization: `Bearer ${authToken}` },
      }
    );
    return response.data;
  } catch (error) {
    handleAxiosError(error);
  }
}

export async function getAppRatingEligibility({
  authToken,
}: AuthFuncParams<void>): Promise<AppRatingEligibilityResponse> {
  try {
    const response = await axiosInstance.get<AppRatingEligibilityResponse>(
      "/auth/nps/app-rating-eligibility",
      {
        headers: { Authorization: `Bearer ${authToken}` },
      }
    );
    return response.data;
  } catch (error) {
    handleAxiosError(error);
  }
}

export async function postWordError({
  data,
  authToken,
}: AuthFuncParams<PostWordErrorRequest>): Promise<GenericMessageResponse> {
  const request = data;
  console.log("postWordError", request);
  try {
    const response = await axiosInstance.post<GenericMessageResponse>(
      "/auth/worderror",
      request,
      {
        headers: { Authorization: `Bearer ${authToken}` },
      }
    );
    return response.data;
  } catch (error) {
    handleAxiosError(error);
  }
}

export async function getPhrase({
  data,
  authToken,
}: AuthFuncParams<GetPhraseRequest>): Promise<GetPhraseResponse> {
  try {
    const response = await axiosInstance.post<GetPhraseResponse>(
      "/auth/phrase",
      data,
      {
        headers: { Authorization: `Bearer ${authToken}` },
      }
    );
    return response.data;
  } catch (error) {
    handleAxiosError(error);
  }
}

export async function putPhraseSaved({
  data,
  authToken,
}: AuthFuncParams<PutPhraseSavedRequest>): Promise<GenericMessageResponse> {
  try {
    const response = await axiosInstance.put<GenericMessageResponse>(
      "/auth/phrase/saved",
      data,
      {
        headers: { Authorization: `Bearer ${authToken}` },
      }
    );
    return response.data;
  } catch (error) {
    handleAxiosError(error);
  }
}

export async function putUserPhraseStatus({
  data,
  authToken,
}: AuthFuncParams<PutUserPhraseStatusRequest>): Promise<GenericMessageResponse> {
  try {
    const response = await axiosInstance.put<GenericMessageResponse>(
      "/auth/phrase/status",
      data,
      {
        headers: { Authorization: `Bearer ${authToken}` },
      }
    );
    return response.data;
  } catch (error) {
    handleAxiosError(error);
  }
}

export async function deleteUserPhrase({
  data,
  authToken,
}: AuthFuncParams<{ phraseId: number }>): Promise<GenericMessageResponse> {
  if (!data?.phraseId) {
    throw new Error("Phrase ID is required");
  }
  try {
    const response = await axiosInstance.delete<GenericMessageResponse>(
      "/auth/phrase/" + data.phraseId,
      {
        headers: { Authorization: `Bearer ${authToken}` },
      }
    );
    return response.data;
  } catch (error) {
    handleAxiosError(error);
  }
}

export async function getKnownPhrases({
  authToken,
  data,
}: AuthFuncParams<{
  page?: number;
  limit?: number;
}>): Promise<GetAllPhrasesResponse> {
  try {
    const params = new URLSearchParams();
    if (data) {
      if (data.page) params.append("page", data.page.toString());
      if (data.limit) params.append("limit", data.limit.toString());
    }

    const response = await axiosInstance.get<GetAllPhrasesResponse>(
      `/auth/phrases/known?${params.toString()}`,
      {
        headers: { Authorization: `Bearer ${authToken}` },
      }
    );
    return response.data;
  } catch (error) {
    handleAxiosError(error);
  }
}

export async function getLearningPhrases({
  authToken,
  data,
}: AuthFuncParams<{
  page?: number;
  limit?: number;
}>): Promise<GetAllPhrasesResponse> {
  try {
    const params = new URLSearchParams();
    if (data) {
      if (data.page) params.append("page", data.page.toString());
      if (data.limit) params.append("limit", data.limit.toString());
    }

    const response = await axiosInstance.get<GetAllPhrasesResponse>(
      `/auth/phrases/learning?${params.toString()}`,
      {
        headers: { Authorization: `Bearer ${authToken}` },
      }
    );
    return response.data;
  } catch (error) {
    handleAxiosError(error);
  }
}

export async function getVersion(): Promise<string> {
  try {
    const response = await axiosInstance.get<string>("/version");
    return response.data;
  } catch (error) {
    handleAxiosError(error);
  }
}

export async function searchWords({
  authToken,
  data,
}: AuthFuncParams<{
  status: string;
  query: string;
  page?: number;
  limit?: number;
}>): Promise<GetSearchWordsResponse> {
  try {
    const params = new URLSearchParams();
    if (data) {
      if (data.status) params.append("status", data.status);
      if (data.query) params.append("query", data.query);
      if (data.page) params.append("page", data.page.toString());
      if (data.limit) params.append("limit", data.limit.toString());
    }

    const response = await axiosInstance.get<GetSearchWordsResponse>(
      `/auth/words/search?${params.toString()}`,
      {
        headers: { Authorization: `Bearer ${authToken}` },
      }
    );
    return response.data;
  } catch (error) {
    handleAxiosError(error);
  }
}

export async function searchPhrases({
  authToken,
  data,
}: AuthFuncParams<{
  status: string;
  query: string;
  page?: number;
  limit?: number;
}>): Promise<GetAllPhrasesResponse> {
  try {
    const params = new URLSearchParams();
    if (data) {
      if (data.status) params.append("status", data.status);
      if (data.query) params.append("query", data.query);
      if (data.page) params.append("page", data.page.toString());
      if (data.limit) params.append("limit", data.limit.toString());
    }

    const response = await axiosInstance.get<GetSearchPhrasesResponse>(
      `/auth/phrases/search?${params.toString()}`,
      {
        headers: { Authorization: `Bearer ${authToken}` },
      }
    );
    return response.data;
  } catch (error) {
    handleAxiosError(error);
  }
}

export async function deleteUserWord({
  authToken,
  data,
}: AuthFuncParams<{ userWordId: number }>): Promise<GenericMessageResponse> {
  if (!data?.userWordId) {
    throw new Error("User word id is required");
  }
  try {
    const response = await axiosInstance.delete<GenericMessageResponse>(
      `/auth/word/${data.userWordId}`,
      {
        headers: { Authorization: `Bearer ${authToken}` },
      }
    );
    return response.data;
  } catch (error) {
    handleAxiosError(error);
  }
}

export async function submitOnboarding({
  data,
  authToken,
}: AuthFuncParams<
  Omit<Onboarding, "id" | "userId" | "createdAt">
>): Promise<GenericMessageResponse> {
  try {
    const request: PostOnboardingRequest = {
      onboarding: data as Onboarding, // Type assertion as API expects the full type
    };

    const response = await axiosInstance.post<GenericMessageResponse>(
      "/auth/onboarding",
      request,
      {
        headers: { Authorization: `Bearer ${authToken}` },
      }
    );
    return response.data;
  } catch (error) {
    handleAxiosError(error);
  }
}

// User Settings API calls
export async function getUserSettings({
  authToken,
}: AuthFuncParams<void>): Promise<GetUserSettingsResponse> {
  try {
    const response = await axiosInstance.get<GetUserSettingsResponse>(
      "/auth/user/settings",
      {
        headers: { Authorization: `Bearer ${authToken}` },
      }
    );
    return response.data;
  } catch (error) {
    handleAxiosError(error);
  }
}

export async function updateUserSettings({
  data,
  authToken,
}: AuthFuncParams<PutUserSettingsRequest>): Promise<GenericMessageResponse> {
  try {
    const response = await axiosInstance.put<GenericMessageResponse>(
      "/auth/user/settings",
      data,
      {
        headers: { Authorization: `Bearer ${authToken}` },
      }
    );
    return response.data;
  } catch (error) {
    handleAxiosError(error);
  }
}

export async function getAllCoursesProgress({
  authToken,
}: AuthFuncParams<void>): Promise<GetAllCoursesProgressResponse> {
  try {
    const response = await axiosInstance.get<GetAllCoursesProgressResponse>(
      "/auth/coursesprogress",
      {
        headers: { Authorization: `Bearer ${authToken}` },
      }
    );
    return response.data;
  } catch (error) {
    handleAxiosError(error);
  }
}

export async function getAllCourseTopics({
  authToken,
}: AuthFuncParams<void>): Promise<GetAllCourseTopicsResponse> {
  try {
    const response = await axiosInstance.get("/auth/coursetopics", {
      headers: {
        Authorization: `Bearer ${authToken}`,
      },
    });
    return response.data;
  } catch (error) {
    handleAxiosError(error);
  }
}

export async function getAllCourseLevels({
  authToken,
}: AuthFuncParams<void>): Promise<GetAllCourseLevelsResponse> {
  try {
    const response = await axiosInstance.get("/auth/courselevels", {
      headers: {
        Authorization: `Bearer ${authToken}`,
      },
    });
    return response.data;
  } catch (error) {
    handleAxiosError(error);
  }
}

export async function getCompletedLessons({
  authToken,
}: AuthFuncParams<void>): Promise<GetCompletedLessonsResponse> {
  try {
    const response = await axiosInstance.get<GetCompletedLessonsResponse>(
      "/auth/completedlessons",
      {
        headers: { Authorization: `Bearer ${authToken}` },
      }
    );
    return response.data;
  } catch (error) {
    handleAxiosError(error);
  }
}

export async function postPlacementResult({
  authToken,
  data,
}: AuthFuncParams<PostPlacementResultRequest>): Promise<GenericMessageResponse> {
  try {
    const response = await axiosInstance.post<GenericMessageResponse>(
      "/auth/user/placementresult",
      data,
      {
        headers: { Authorization: `Bearer ${authToken}` },
      }
    );
    return response.data;
  } catch (error) {
    handleAxiosError(error);
  }
}

/**
 * Records a single placement-test node visit for analytics.
 * Fire-and-forget — analytics must never block the placement flow.
 * Mirrors client-native/src/api/Api.ts postPlacementNodeEvent.
 */
export async function postPlacementNodeEvent(data: {
  configVersion: number;
  nodeId: string;
  answer: string;
  stepIndex: number;
}): Promise<void> {
  try {
    if (!tokenStorage.isTokenSet()) return;
    const authToken = tokenStorage.getToken();
    await axiosInstance.post("/auth/placement-event", data, {
      headers: { Authorization: `Bearer ${authToken}` },
    });
  } catch {
    // Silently ignore — analytics should never block the placement flow.
  }
}

/**
 * Marks a tutorial item as completed (e.g. "placement_test").
 * Mirrors client-native/src/api/Api.ts completeTutorialItem.
 */
export async function completeTutorialItem(
  itemId: TutorialItemID
): Promise<GenericMessageResponse> {
  if (!tokenStorage.isTokenSet()) {
    throw new AuthError("Not authenticated");
  }
  const authToken = tokenStorage.getToken();
  const data: PostCompleteTutorialItemRequest = { itemId };
  try {
    const response = await axiosInstance.post<GenericMessageResponse>(
      "/auth/tutorial/complete",
      data,
      {
        headers: { Authorization: `Bearer ${authToken}` },
      }
    );
    return response.data;
  } catch (error) {
    handleAxiosError(error);
  }
}

// ─── Phrase translate / status (PWA parity with client-native) ──────────────
// iOS ref: client-native/src/api/Api.ts getPhrase / putPhraseSaved /
// putUserPhraseStatus. POST /auth/phrase translates a selected phrase; the
// PWA already exposes `getPhrase` (POST under the hood) so `postPhrase` is the
// parity-named alias other tracks can import. Same for `putPhraseStatus`.
export const postPhrase = getPhrase;
export const putPhraseStatus = putUserPhraseStatus;

// ─── Card count v2 ──────────────────────────────────────────────────────────
// iOS ref: client-native/src/api/Api.ts getCardCountV2 — GET /auth/v2/card/count
// returns the count of both word AND phrase review cards (v1 is word-only).
export async function getCardCountV2({
  authToken,
}: AuthFuncParams<void>): Promise<GetCardCountResponse> {
  try {
    const response = await axiosInstance.get<GetCardCountResponse>(
      "/auth/v2/card/count",
      {
        headers: { Authorization: `Bearer ${authToken}` },
      }
    );
    return response.data;
  } catch (error) {
    handleAxiosError(error);
  }
}

// ─── Dictionary translate & bulk add ────────────────────────────────────────
// iOS ref: client-native/src/api/Api.ts postTranslate / postDictionaryBulkAdd.
// POST /auth/translate is the dictionary "add word" translate (distinct from
// /auth/phrase, which translates an in-video phrase selection).
export async function postTranslate({
  data,
  authToken,
}: AuthFuncParams<TranslateRequest>): Promise<TranslateResponse> {
  try {
    const response = await axiosInstance.post<TranslateResponse>(
      "/auth/translate",
      data,
      {
        headers: { Authorization: `Bearer ${authToken}` },
      }
    );
    return response.data;
  } catch (error) {
    handleAxiosError(error);
  }
}

export async function postDictionaryBulkAdd({
  data,
  authToken,
}: AuthFuncParams<BulkAddRequest>): Promise<BulkAddResponse> {
  try {
    const response = await axiosInstance.post<BulkAddResponse>(
      "/auth/dictionary/bulk-add",
      data,
      {
        headers: { Authorization: `Bearer ${authToken}` },
      }
    );
    return response.data;
  } catch (error) {
    handleAxiosError(error);
  }
}

// ─── Video embed check ──────────────────────────────────────────────────────
// iOS ref: client-native/src/api/Api.ts checkVideoEmbed — POST
// /auth/video/check-embed. Backend returns { embeddable: boolean }; used to
// decide whether to render the YouTube iframe or fall back (Cloudflare).
export type CheckEmbedResponse = {
  embeddable: boolean;
};

export async function checkVideoEmbed({
  authToken,
  data,
}: AuthFuncParams<{ youtube_id: string }>): Promise<CheckEmbedResponse> {
  try {
    const response = await axiosInstance.post<CheckEmbedResponse>(
      "/auth/video/check-embed",
      data,
      {
        headers: { Authorization: `Bearer ${authToken}` },
      }
    );
    return response.data;
  } catch (error) {
    handleAxiosError(error);
  }
}

// ─── AI Explainer (SSE streaming) ───────────────────────────────────────────
// iOS ref: server POST /auth/explain streams Server-Sent Events
// (`data: {json}\n\n`) where each JSON is an ExplainStreamEvent of type
// "chunk" | "complete" | "error". EventSource can't set an Authorization
// header, so this uses fetch + a ReadableStream reader and parses the SSE
// frames manually. Consume via the onEvent callback (preferred — gives you the
// final "complete" metadata) or the returned async iterator over chunk text.
//
// How to verify:
//   const stream = getExplain({ authToken, data: { word: "hola",
//     translation: "hello", context: "Hola amigo", conversationHistory: [],
//     isPhrase: false } });
//   for await (const text of stream) console.log(text);
export interface GetExplainOptions {
  /** Called for every parsed SSE event (chunk / complete / error). */
  onEvent?: (event: ExplainStreamEvent) => void;
  /** Convenience callback for just the text deltas of "chunk" events. */
  onChunk?: (chunk: string) => void;
  /** Abort the in-flight stream (e.g. on unmount). */
  signal?: AbortSignal;
}

export async function* getExplain({
  authToken,
  data,
  onEvent,
  onChunk,
  signal,
}: AuthFuncParams<ExplainRequest> & GetExplainOptions): AsyncGenerator<
  string,
  void,
  unknown
> {
  const response = await fetch(apiUrl("auth/explain"), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "text/event-stream",
      Authorization: `Bearer ${authToken}`,
      [ClientUpdateHeaderKey]: VERSION,
      [DevicePlatformHeader]: "web",
    },
    body: JSON.stringify(data),
    signal,
  });

  if (response.status === 401) {
    tokenStorage.clearToken();
    window.location.assign(BAD_AUTH_REDIRECT_URL);
    throw new AuthError("unauthorized");
  }
  if (!response.ok || !response.body) {
    throw new Error(`Explain request failed (status ${response.status})`);
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  const dispatch = (event: ExplainStreamEvent) => {
    onEvent?.(event);
    if (event.type === "chunk" && event.content) {
      onChunk?.(event.content);
    }
  };

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      // SSE frames are separated by a blank line ("\n\n").
      let sep: number;
      while ((sep = buffer.indexOf("\n\n")) !== -1) {
        const frame = buffer.slice(0, sep);
        buffer = buffer.slice(sep + 2);

        for (const line of frame.split("\n")) {
          const trimmed = line.trimStart();
          if (!trimmed.startsWith("data:")) continue;
          const payload = trimmed.slice(5).trim();
          if (!payload || payload === "[DONE]") continue;
          let event: ExplainStreamEvent;
          try {
            event = JSON.parse(payload) as ExplainStreamEvent;
          } catch {
            continue;
          }
          dispatch(event);
          if (event.type === "chunk" && event.content) {
            yield event.content;
          }
          if (event.type === "error") {
            throw new Error(event.error || "Explain stream error");
          }
        }
      }
    }
  } finally {
    reader.releaseLock();
  }
}
